import React, { createContext, useContext, ReactNode, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { SocketEventData } from '../types/socket';
import { io, Socket } from 'socket.io-client';

interface SocketContextType {
  socketEvent: SocketEventData | null;
  clearSocketEvent: () => void;
  isConnected: boolean;
  connectionError: string | null;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

export const SocketProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { business, refreshBusinessProfile } = useAuth();
  const [socketEvent, setSocketEvent] = useState<SocketEventData | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const socketRef = useRef<Socket | null>(null);

  const clearSocketEvent = useCallback(() => {
    setSocketEvent(null);
  }, []);

  useEffect(() => {
    if (!business?._id) {
      console.log('Socket: Business ID yok, bağlantı kurulmuyor');
      return;
    }

    // Socket URL'ini belirle - localhost için özel kontrol
    // NOT: Server'ın CORS ayarlarında client'ın çalıştığı origin'in olması gerekiyor
    // Server 3001 portunda çalışıyor
    let socketUrl = process.env.REACT_APP_SOCKET_URL || 'http://localhost:3001';
    
    // Token'ı localStorage'dan al (eğer varsa)
    const token = localStorage.getItem('businessToken');
    
    console.log('Socket: Bağlantı kuruluyor...', { 
      socketUrl, 
      businessId: business._id,
      hasToken: !!token,
      currentOrigin: window.location.origin
    });
    
    // Polling'i öncelikli yap, websocket başarısız olursa polling kullanılır
    // Localhost'ta test ederken path'i açıkça belirt
    const socketInstance = io(socketUrl, {
      transports: ['polling', 'websocket'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
      timeout: 20000,
      forceNew: false,
      autoConnect: true,
      // Socket.io path'i (varsayılan: /socket.io/)
      path: '/socket.io/',
      // Upgrade timeout (polling'den websocket'e geçiş için)
      upgrade: true,
      // Remember transport (son kullanılan transport'u hatırla)
      rememberUpgrade: false,
      // Authentication - token varsa gönder (server'da kullanılıyorsa)
      auth: token ? { token } : undefined,
      // Extra headers - token varsa Authorization header'ı ekle
      // NOT: extraHeaders sadece polling transport'unda çalışır, websocket'te çalışmaz
      extraHeaders: token ? {
        Authorization: `Bearer ${token}`
      } : undefined,
      // Query parameters - businessId'yi query'de de gönder (bazı server'lar bunu bekler)
      query: {
        businessId: business._id
      },
      // CORS için withCredentials
      withCredentials: true
    });

    // Socket instance oluşturuldu - ID henüz undefined (bağlantı kurulunca atanacak)
    console.log('Socket: Instance oluşturuldu, bağlantı bekleniyor...', { 
      connected: socketInstance.connected,
      id: socketInstance.id 
    });

    // Connection events
    socketInstance.on('connect', () => {
      console.log('✅ Socket connected!', { 
        socketId: socketInstance.id,
        transport: (socketInstance.io.engine as any)?.transport?.name 
      });
      setIsConnected(true);
      setConnectionError(null);
      
      // Bağlantı kurulduktan sonra kısa bir süre bekle, sonra room'a katıl
      // Bu, server'ın bağlantıyı tam olarak hazırlaması için zaman verir
      setTimeout(() => {
        console.log('📤 Emitting join-business:', { businessId: business._id });
        
        // Server callback göndermiyor, sadece 'joined' event'i emit ediyor
        // Bu yüzden callback kullanmıyoruz
        socketInstance.emit('join-business', { businessId: business._id });
      }, 100); // 100ms bekle
    });

    socketInstance.on('connect_error', (error: any) => {
      console.error('❌ Socket connection error:', {
        message: error.message,
        type: error.type,
        description: error.description,
        context: error.context,
        data: error.data,
        error: error
      });
      setIsConnected(false);
      
      // Hata mesajını daha anlaşılır hale getir
      let errorMessage = 'Socket bağlantı hatası';
      if (error.message) {
        if (error.message.includes('server error')) {
          // Server error genellikle server tarafında bir sorun olduğunu gösterir
          // Bu durumda server log'larını kontrol etmek gerekir
          errorMessage = 'Socket sunucusu hatası. Sunucu log\'larını kontrol edin. Muhtemelen join-business event\'i handle edilirken bir hata oluştu.';
        } else if (error.message.includes('xhr poll error')) {
          errorMessage = 'Socket sunucusuna bağlanılamıyor. Sunucu çalışıyor mu?';
        } else {
          errorMessage = `Bağlantı hatası: ${error.message}`;
        }
      } else if (error.type) {
        errorMessage = `Bağlantı hatası: ${error.type}`;
      }
      
      // Eğer error.data varsa, bu server'dan gelen ek bilgi olabilir
      if (error.data) {
        console.error('Server error data:', error.data);
        if (typeof error.data === 'string') {
          errorMessage += ` - ${error.data}`;
        } else if (error.data.message) {
          errorMessage += ` - ${error.data.message}`;
        }
      }
      
      setConnectionError(errorMessage);
      
      // WebSocket başarısız olursa polling'e geç
      const transports = socketInstance.io.opts.transports;
      if (transports && Array.isArray(transports) && transports.includes('websocket' as any)) {
        console.log('🔄 Falling back to polling transport');
        // Transport'u polling'e zorla
        socketInstance.io.opts.transports = ['polling' as any];
      }
    });

    socketInstance.on('disconnect', (reason: string) => {
      console.log('Socket disconnected:', reason);
      setIsConnected(false);
      
      if (reason === 'io server disconnect') {
        // Server tarafından bağlantı kesildi, yeniden bağlan
        setConnectionError('Sunucu bağlantıyı kapattı. Yeniden bağlanılıyor...');
        socketInstance.connect();
      } else if (reason === 'transport close') {
        setConnectionError('Bağlantı kapatıldı. Yeniden bağlanılıyor...');
      } else if (reason === 'transport error') {
        setConnectionError('Bağlantı hatası. Yeniden bağlanılıyor...');
      }
    });

    socketInstance.on('reconnect', (attemptNumber: number) => {
      console.log('Socket reconnected after', attemptNumber, 'attempts');
      setIsConnected(true);
      setConnectionError(null);
      // Yeniden bağlandıktan sonra room'a katıl
      socketInstance.emit('join-business', { businessId: business._id });
    });

    socketInstance.on('reconnect_attempt', (attemptNumber: number) => {
      console.log('Socket reconnection attempt:', attemptNumber);
    });

    socketInstance.on('reconnect_error', (error: any) => {
      console.error('Socket reconnection error:', error);
    });

    socketInstance.on('reconnect_failed', () => {
      console.error('Socket reconnection failed');
      setIsConnected(false);
      setConnectionError('Yeniden bağlanma başarısız oldu. Lütfen sayfayı yenileyin.');
    });

    socketInstance.on('error', (error: any) => {
      console.error('Socket error event:', error);
      setIsConnected(false);
      setConnectionError(`Socket hatası: ${error.message || 'Bilinmeyen hata'}`);
    });

    // Server'dan gelen özel hata mesajlarını dinle
    socketInstance.on('exception', (data: any) => {
      console.error('❌ Socket exception:', data);
      setIsConnected(false);
      setConnectionError(`Sunucu hatası: ${data.message || data.error || 'Bilinmeyen hata'}`);
    });

    // Server'dan gelen disconnect_with_error event'ini dinle
    socketInstance.on('disconnect_with_error', (data: any) => {
      console.error('❌ Socket disconnect_with_error:', data);
      setIsConnected(false);
      setConnectionError(`Sunucu bağlantıyı kapattı: ${data.message || data.error || 'Bilinmeyen hata'}`);
    });

    socketInstance.on('connect_timeout', () => {
      console.error('Socket connection timeout');
      setIsConnected(false);
      setConnectionError('Bağlantı zaman aşımına uğradı');
    });

    // Sync durumlarını dinle
    socketInstance.on('sync-status', (data: SocketEventData) => {
      console.log('Sync Status:', data);
      // Sadece completed veya failed durumlarında modal göster
      if (data.action === 'completed' || data.action === 'failed') {
        setSocketEvent(data);
      }
    });

    // Analysis durumlarını dinle
    socketInstance.on('analysis-status', (data: SocketEventData) => {
      console.log('Analysis Status:', data);
      
      // Eğer analysis started ise, business profile'ı yenile
      if (data.action === 'started') {
        console.log('Analysis started, refreshing business profile...');
        refreshBusinessProfile().catch((error) => {
          console.error('Failed to refresh business profile:', error);
        });
      }
      
      // Sadece completed veya failed durumlarında modal göster
      if (data.action === 'completed' || data.action === 'failed') {
        setSocketEvent(data);
      }
    });

    // Server'dan gelen 'joined' event'ini dinle (join-business başarılı olduğunda)
    socketInstance.on('joined', (data: any) => {
      console.log('✅ Successfully joined business room:', data);
    });

    // Server'dan gelen error event'ini dinle
    socketInstance.on('error', (data: any) => {
      console.error('❌ Socket error event from server:', data);
      if (data.message) {
        setConnectionError(`Sunucu hatası: ${data.message}`);
        setIsConnected(false);
      }
    });

    socketRef.current = socketInstance;

    // Cleanup on unmount
    return () => {
      if (socketRef.current) {
        socketRef.current.off('sync-status');
        socketRef.current.off('analysis-status');
        socketRef.current.off('connect');
        socketRef.current.off('connect_error');
        socketRef.current.off('disconnect');
        socketRef.current.off('reconnect');
        socketRef.current.off('reconnect_attempt');
        socketRef.current.off('reconnect_error');
        socketRef.current.off('reconnect_failed');
        socketRef.current.off('error');
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [business?._id, refreshBusinessProfile]);

  return (
    <SocketContext.Provider value={{ socketEvent, clearSocketEvent, isConnected, connectionError }}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

